import { type ReactNode, type Ref, type RefObject, useCallback } from 'react';
import {
	clearAllBodyScrollLocks,
	disableBodyScroll,
	enableBodyScroll,
} from 'body-scroll-lock';

import Portal from 'components/Portal';
import { MODALS_CONTAINER_ID } from 'constants/ids';
import {
	useEffectOnce,
	useId,
	useRefObject,
	useUnmount,
	useValueChangeEffect,
} from 'hooks';
import { useRouteChange } from 'hooks/navigation';
import { cnm } from 'utils/classNames';
import { afterNextPaint, is } from 'utils/helpers';

interface BaseProps {
	children?: ReactNode;
	className?: string;
	dataCy?: string;
	id?: string;
	isOpen: boolean;
	onClose: () => void;
	onOpen?: () => void;
	ref?: Ref<HTMLDialogElement>;
	scrollLockRef?: RefObject<HTMLElement | null>;
	shouldCloseOnOutsideClick?: boolean;
}

interface WithLabel extends BaseProps {
	ariaLabel: string;
	ariaLabelledby?: never;
}
interface WithLabelledBy extends BaseProps {
	ariaLabel?: never;
	ariaLabelledby: string;
}

type Props = WithLabel | WithLabelledBy;

export const MODAL_DIALOG_TOUCH_MOVE_WHITELIST_CLASS =
	'modal-dialog-touch-move-whitelist';

export default function ModalDialog({
	ariaLabel,
	ariaLabelledby,
	children,
	className,
	dataCy,
	id: idProp,
	isOpen,
	onClose,
	onOpen,
	ref,
	scrollLockRef,
	shouldCloseOnOutsideClick = true,
}: Props) {
	const dialogRef = useRefObject<HTMLDialogElement | null>(ref);
	const fallbackId = useId('modal-dialog');
	const id = idProp || fallbackId;

	// The scrollLockRef is needed for Safari to specifically lock the element
	// that may overflow. Locking the dialog root and whitelisting with the
	// allowTouchMove class doesn't work as a lock if the element is not
	// scrollable.
	// This library should not be needed at all when Safari supports containing
	// overscroll on non-scrollable elements.
	// https://bugs.webkit.org/show_bug.cgi?id=243452
	// Not using useRefObject since the ref will not be attached internally.
	const onDialogOpen = useCallback(() => {
		const lockEl = scrollLockRef?.current ?? dialogRef.current;
		if (lockEl) {
			disableBodyScroll(lockEl, {
				reserveScrollBarGap: true,
				allowTouchMove: (el) =>
					el
						? Boolean(el.closest(`.${MODAL_DIALOG_TOUCH_MOVE_WHITELIST_CLASS}`))
						: false,
			});
		}
		onOpen?.();
	}, [dialogRef, scrollLockRef, onOpen]);

	useRouteChange(() => {
		onClose();
	});

	const clearScrollLocks = useCallback(() => {
		const lockEl = scrollLockRef?.current ?? dialogRef.current;
		if (lockEl) {
			enableBodyScroll(lockEl);
		} else {
			// Nuclear fallback to avoid locking the site.
			clearAllBodyScrollLocks();
		}
	}, [dialogRef, scrollLockRef]);

	useUnmount(() => {
		clearScrollLocks();
	});

	// Open on initial render.
	useEffectOnce(() => {
		// Both the ref object hook and the portal prevent the ref from being set
		// immediately, wait for paint to handle it.
		afterNextPaint(() => {
			if (isOpen && dialogRef.current) {
				dialogRef.current.showModal();
				onDialogOpen();
			}
		});
	});

	useValueChangeEffect(isOpen, () => {
		if (!dialogRef.current) {
			return;
		}
		if (isOpen) {
			dialogRef.current.showModal();
			onDialogOpen();
		} else {
			dialogRef.current.close();
			clearScrollLocks();
		}
	});

	return (
		// TODO: Is the portal still needed for <dialog>? Placement in the top
		// layer and inert background is guaranteed, but a nested dialog may
		// inherit styles from parent elements and events will bubble which
		// could have unexpected side effects.
		<Portal selector={`#${MODALS_CONTAINER_ID}`} portalKey={id}>
			{/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
			<dialog
				ref={dialogRef}
				aria-label={ariaLabel}
				aria-labelledby={ariaLabelledby}
				className={cnm(
					'max-h-full max-w-full bg-unset backdrop:bg-shadow/20',
					className,
				)}
				// Capture all expected ways of closing the dialog natively, like
				// Escape key and requestClose(). Stop the event to prevent the browser
				// from closing the dialog, instead triggering the handler that should
				// reset the state which will trigger the isOpen effect.
				// Check the target to avoid reacting to bubbling cancel events from
				// children, like pressing cancel in a file input upload window.
				onCancel={(e) => {
					if (e.target === dialogRef.current) {
						e.preventDefault();
						onClose();
					}
				}}
				// If something unexpected triggers a close on an open dialog, let it
				// trigger an onClose to 'resync' the states.
				onClose={() => {
					if (isOpen) {
						onClose();
					}
				}}
				// There is no way to detect clicks on the ::backdrop for 'true outside
				// clicks', but any dialog children should have their own container
				// elements so in practice any clicks on the dialog itself are equal
				// to clicks on the ::backdrop and therefore outside clicks that should
				// trigger a close.
				onClick={(event) => {
					const { target } = event;
					if (
						shouldCloseOnOutsideClick &&
						is.element(target) &&
						target.nodeName === 'DIALOG'
					) {
						onClose();
					}
				}}
				data-cy={dataCy}
			>
				{children}
			</dialog>
		</Portal>
	);
}
ModalDialog.displayName = 'ModalDialog';
